import { getApiBaseUrl } from '../lib/api';
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { ArrowLeft, Save, Edit2, X, ChevronDown, ChevronUp } from 'lucide-react';

const API_URL = getApiBaseUrl();

// Collapsible Section Component for mobile - defined outside main component
const CollapsibleSection = ({ title, isOpen, onToggle, bgColor, children }) => (
  <div className={`${bgColor} rounded-lg overflow-hidden`}>
    <button
      onClick={onToggle}
      className="w-full p-3 sm:p-4 flex items-center justify-between text-left"
    >
      <h3 className="text-sm sm:text-base font-semibold">{title}</h3>
      {isOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
    </button>
    {isOpen && <div className="px-3 sm:px-4 pb-3 sm:pb-4">{children}</div>}
  </div>
);

// Input Field Component - defined outside main component
const InputField = ({ label, value, onChange, disabled, type = "number", step }) => (
  <div className="space-y-1">
    <label className="block text-xs sm:text-sm font-medium text-slate-700">{label}</label>
    <input
      type={type}
      step={step}
      value={value || ''}
      onChange={onChange}
      disabled={disabled}
      className="w-full p-2 sm:p-2.5 text-sm border rounded-lg bg-white disabled:bg-slate-50 disabled:text-slate-500"
    />
  </div>
);

const AdminConfig = () => {
  const [activeTab, setActiveTab] = useState('fees');
  const [feeMatrix, setFeeMatrix] = useState(null);
  const [repairMarkup, setRepairMarkup] = useState(null);
  const [scoringMatrix, setScoringMatrix] = useState(null);
  const [jurisdictions, setJurisdictions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editingMarkup, setEditingMarkup] = useState(false);
  const [editingScoring, setEditingScoring] = useState(false);
  const [editingJurisdictions, setEditingJurisdictions] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    management: true,
    listing: false,
    distribution: false,
    service: false
  });

  const getToken = () => localStorage.getItem('access_token') || localStorage.getItem('token');

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  useEffect(() => {
    fetchAllConfigs();
  }, []);

  const fetchAllConfigs = async () => {
    setLoading(true);
    try {
      const token = getToken();
      const headers = { 'Authorization': `Bearer ${token}` };

      const [feesRes, markupRes, scoringRes, jurisdRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/config/fee-matrix`, { headers }),
        fetch(`${API_URL}/api/admin/config/repair-markup`, { headers }),
        fetch(`${API_URL}/api/admin/config/scoring-matrix`, { headers }),
        fetch(`${API_URL}/api/admin/config/jurisdictions`, { headers })
      ]);

      if (feesRes.ok) setFeeMatrix(await feesRes.json());
      if (markupRes.ok) setRepairMarkup(await markupRes.json());
      if (scoringRes.ok) setScoringMatrix(await scoringRes.json());
      if (jurisdRes.ok) setJurisdictions(await jurisdRes.json());
    } catch (error) {
      console.error('Error fetching configs:', error);
      toast.error('Failed to load configurations');
    } finally {
      setLoading(false);
    }
  };

  const updateFeeMatrix = async (updates) => {
    try {
      const token = getToken();
      const response = await fetch(`${API_URL}/api/admin/config/fee-matrix`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ ...feeMatrix, ...updates })
      });

      if (response.ok) {
        toast.success('Fee matrix updated');
        fetchAllConfigs();
        setEditing(false);
      } else {
        throw new Error('Update failed');
      }
    } catch (error) {
      toast.error('Failed to update fee matrix');
    }
  };

  const updateRepairMarkup = async () => {
    try {
      const token = getToken();
      const response = await fetch(`${API_URL}/api/admin/config/repair-markup`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(repairMarkup)
      });

      if (response.ok) {
        toast.success('Repair markup updated');
        fetchAllConfigs();
        setEditingMarkup(false);
      } else {
        throw new Error('Update failed');
      }
    } catch (error) {
      toast.error('Failed to update repair markup');
    }
  };

  const updateScoringMatrix = async () => {
    try {
      const token = getToken();
      const response = await fetch(`${API_URL}/api/admin/config/scoring-matrix`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(scoringMatrix)
      });

      if (response.ok) {
        toast.success('Scoring matrix updated');
        fetchAllConfigs();
        setEditingScoring(false);
      } else {
        throw new Error('Update failed');
      }
    } catch (error) {
      toast.error('Failed to update scoring matrix');
    }
  };

  const updateJurisdiction = async (jurisdiction) => {
    try {
      const token = getToken();
      const response = await fetch(`${API_URL}/api/admin/config/jurisdictions/${jurisdiction.jurisdiction}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(jurisdiction)
      });

      if (response.ok) {
        toast.success('Jurisdiction updated');
        fetchAllConfigs();
      } else {
        throw new Error('Update failed');
      }
    } catch (error) {
      toast.error('Failed to update jurisdiction');
    }
  };

  // Inline components are fine since we use them directly in JSX

  const renderFeeMatrix = () => (
    <Card className="p-4 sm:p-6">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-4 sm:mb-6">
        <h2 className="text-lg sm:text-2xl font-bold">Fee Matrix</h2>
        <Button 
          onClick={() => setEditing(!editing)} 
          size="sm"
          variant={editing ? "outline" : "default"}
          className="w-full sm:w-auto"
        >
          {editing ? <><X className="w-4 h-4 mr-1" />Cancel</> : <><Edit2 className="w-4 h-4 mr-1" />Edit</>}
        </Button>
      </div>

      {feeMatrix && (
        <div className="space-y-3">
          {/* Management Fees */}
          <CollapsibleSection
            title="Management Fees"
            isOpen={expandedSections.management}
            onToggle={() => toggleSection('management')}
            bgColor="bg-blue-50"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <InputField
                label="Semi-Managed (%)"
                value={feeMatrix.semi_managed_fee_percent}
                onChange={(e) => setFeeMatrix({...feeMatrix, semi_managed_fee_percent: parseFloat(e.target.value)})}
                disabled={!editing}
                step="0.1"
              />
              <InputField
                label="Fully-Managed (%)"
                value={feeMatrix.fully_managed_fee_percent}
                onChange={(e) => setFeeMatrix({...feeMatrix, fully_managed_fee_percent: parseFloat(e.target.value)})}
                disabled={!editing}
                step="0.1"
              />
            </div>
          </CollapsibleSection>

          {/* Listing Fees */}
          <CollapsibleSection
            title="Listing Fees"
            isOpen={expandedSections.listing}
            onToggle={() => toggleSection('listing')}
            bgColor="bg-slate-50"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <InputField
                label="Base Listing Fee (£)"
                value={feeMatrix.base_listing_fee}
                onChange={(e) => setFeeMatrix({...feeMatrix, base_listing_fee: parseFloat(e.target.value)})}
                disabled={!editing}
              />
              <InputField
                label="Admin Fee (£)"
                value={feeMatrix.admin_fee}
                onChange={(e) => setFeeMatrix({...feeMatrix, admin_fee: parseFloat(e.target.value)})}
                disabled={!editing}
              />
            </div>
          </CollapsibleSection>

          {/* Distribution Channel Fees */}
          <CollapsibleSection
            title="Distribution Channels"
            isOpen={expandedSections.distribution}
            onToggle={() => toggleSection('distribution')}
            bgColor="bg-purple-50"
          >
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <InputField
                label="Rightmove (£)"
                value={feeMatrix.rightmove_fee}
                onChange={(e) => setFeeMatrix({...feeMatrix, rightmove_fee: parseFloat(e.target.value)})}
                disabled={!editing}
              />
              <InputField
                label="Zoopla (£)"
                value={feeMatrix.zoopla_fee}
                onChange={(e) => setFeeMatrix({...feeMatrix, zoopla_fee: parseFloat(e.target.value)})}
                disabled={!editing}
              />
              <InputField
                label="Gumtree (£)"
                value={feeMatrix.gumtree_fee}
                onChange={(e) => setFeeMatrix({...feeMatrix, gumtree_fee: parseFloat(e.target.value)})}
                disabled={!editing}
              />
            </div>
          </CollapsibleSection>

          {/* Service Fees */}
          <CollapsibleSection
            title="Service Fees"
            isOpen={expandedSections.service}
            onToggle={() => toggleSection('service')}
            bgColor="bg-yellow-50"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <InputField
                label="Compliance Base (£)"
                value={feeMatrix.compliance_base_fee}
                onChange={(e) => setFeeMatrix({...feeMatrix, compliance_base_fee: parseFloat(e.target.value)})}
                disabled={!editing}
              />
              <InputField
                label="Inspection (£)"
                value={feeMatrix.inspection_fee}
                onChange={(e) => setFeeMatrix({...feeMatrix, inspection_fee: parseFloat(e.target.value)})}
                disabled={!editing}
              />
              <InputField
                label="Rent Protection (%)"
                value={feeMatrix.rent_protection_insurance_percent}
                onChange={(e) => setFeeMatrix({...feeMatrix, rent_protection_insurance_percent: parseFloat(e.target.value)})}
                disabled={!editing}
                step="0.1"
              />
              <InputField
                label="Home Emergency (£/mo)"
                value={feeMatrix.home_emergency_cover_monthly}
                onChange={(e) => setFeeMatrix({...feeMatrix, home_emergency_cover_monthly: parseFloat(e.target.value)})}
                disabled={!editing}
              />
            </div>
          </CollapsibleSection>

          {editing && (
            <div className="flex flex-col sm:flex-row gap-2 pt-4">
              <Button variant="outline" onClick={() => { fetchAllConfigs(); setEditing(false); }} className="w-full sm:w-auto">
                Cancel
              </Button>
              <Button onClick={() => updateFeeMatrix(feeMatrix)} className="w-full sm:w-auto">
                <Save className="w-4 h-4 mr-2" />Save Changes
              </Button>
            </div>
          )}

          <div className="mt-4 p-3 bg-slate-100 rounded-lg">
            <p className="text-xs text-slate-600">
              v{feeMatrix.version} • {new Date(feeMatrix.created_at).toLocaleDateString()}
            </p>
          </div>
        </div>
      )}
    </Card>
  );

  const renderRepairMarkup = () => (
    <Card className="p-4 sm:p-6">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-4 sm:mb-6">
        <h2 className="text-lg sm:text-2xl font-bold">Repair Markup</h2>
        <Button 
          onClick={() => setEditingMarkup(!editingMarkup)} 
          size="sm"
          variant={editingMarkup ? "outline" : "default"}
          className="w-full sm:w-auto"
        >
          {editingMarkup ? <><X className="w-4 h-4 mr-1" />Cancel</> : <><Edit2 className="w-4 h-4 mr-1" />Edit</>}
        </Button>
      </div>

      {repairMarkup && (
        <div className="space-y-4">
          <div className="bg-blue-50 p-3 sm:p-4 rounded-lg">
            <h3 className="font-semibold text-sm sm:text-base mb-3">Markup Tiers</h3>
            <div className="space-y-3">
              {repairMarkup.markup_tiers?.map((tier, idx) => (
                <div key={idx} className="flex flex-col sm:flex-row sm:items-center gap-2 p-2 bg-white rounded border">
                  <div className="flex items-center gap-2 flex-1">
                    <span className="text-xs sm:text-sm text-slate-600">Up to £</span>
                    <input
                      type="number"
                      value={tier.max_amount}
                      onChange={(e) => {
                        const newTiers = [...repairMarkup.markup_tiers];
                        newTiers[idx].max_amount = parseFloat(e.target.value);
                        setRepairMarkup({...repairMarkup, markup_tiers: newTiers});
                      }}
                      disabled={!editingMarkup}
                      className="w-20 sm:w-24 p-1.5 text-sm border rounded"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      value={tier.markup_percent}
                      onChange={(e) => {
                        const newTiers = [...repairMarkup.markup_tiers];
                        newTiers[idx].markup_percent = parseFloat(e.target.value);
                        setRepairMarkup({...repairMarkup, markup_tiers: newTiers});
                      }}
                      disabled={!editingMarkup}
                      className="w-16 p-1.5 text-sm border rounded"
                    />
                    <span className="text-xs sm:text-sm font-semibold text-blue-600">% markup</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-red-50 p-3 sm:p-4 rounded-lg">
            <h3 className="font-semibold text-sm sm:text-base mb-2">Emergency Markup</h3>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs sm:text-sm">Additional</span>
              <input
                type="number"
                value={repairMarkup.emergency_markup_additional}
                onChange={(e) => setRepairMarkup({...repairMarkup, emergency_markup_additional: parseFloat(e.target.value)})}
                disabled={!editingMarkup}
                className="w-16 p-1.5 text-sm border rounded"
              />
              <span className="text-xs sm:text-sm font-bold text-red-600">% for emergencies</span>
            </div>
          </div>

          {editingMarkup && (
            <div className="flex flex-col sm:flex-row gap-2 pt-2">
              <Button variant="outline" onClick={() => { fetchAllConfigs(); setEditingMarkup(false); }} className="w-full sm:w-auto">Cancel</Button>
              <Button onClick={updateRepairMarkup} className="w-full sm:w-auto"><Save className="w-4 h-4 mr-2" />Save</Button>
            </div>
          )}
        </div>
      )}
    </Card>
  );

  const renderScoringMatrix = () => (
    <Card className="p-4 sm:p-6">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-4 sm:mb-6">
        <h2 className="text-lg sm:text-2xl font-bold">Tenant Scoring</h2>
        <Button 
          onClick={() => setEditingScoring(!editingScoring)} 
          size="sm"
          variant={editingScoring ? "outline" : "default"}
          className="w-full sm:w-auto"
        >
          {editingScoring ? <><X className="w-4 h-4 mr-1" />Cancel</> : <><Edit2 className="w-4 h-4 mr-1" />Edit</>}
        </Button>
      </div>

      {scoringMatrix && (
        <div className="space-y-4">
          <div className="bg-green-50 p-3 sm:p-4 rounded-lg">
            <h3 className="font-semibold text-sm sm:text-base mb-3">Check Weights (%)</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[
                { key: 'kyc_weight', label: 'KYC' },
                { key: 'aml_weight', label: 'AML' },
                { key: 'credit_check_weight', label: 'Credit Check' },
                { key: 'income_verification_weight', label: 'Income Verification' },
                { key: 'reference_check_weight', label: 'Reference Check' }
              ].map(item => (
                <div key={item.key} className="flex items-center justify-between p-2 bg-white rounded border">
                  <span className="text-xs sm:text-sm">{item.label}</span>
                  <div className="flex items-center gap-1">
                    <input
                      type="number"
                      value={scoringMatrix[item.key]}
                      onChange={(e) => setScoringMatrix({...scoringMatrix, [item.key]: parseFloat(e.target.value)})}
                      disabled={!editingScoring}
                      className="w-14 p-1.5 text-sm border rounded text-right"
                    />
                    <span className="text-xs">%</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-3 gap-2 sm:gap-4">
            <div className="bg-green-100 p-2 sm:p-4 rounded-lg text-center">
              <div className="text-xs sm:text-sm mb-1">Pass</div>
              <input
                type="number"
                value={scoringMatrix.pass_threshold}
                onChange={(e) => setScoringMatrix({...scoringMatrix, pass_threshold: parseInt(e.target.value)})}
                disabled={!editingScoring}
                className="w-12 sm:w-16 p-1 border rounded text-center text-sm sm:text-lg font-bold text-green-700"
              />
            </div>
            <div className="bg-yellow-100 p-2 sm:p-4 rounded-lg text-center">
              <div className="text-xs sm:text-sm mb-1">Refer</div>
              <input
                type="number"
                value={scoringMatrix.refer_threshold}
                onChange={(e) => setScoringMatrix({...scoringMatrix, refer_threshold: parseInt(e.target.value)})}
                disabled={!editingScoring}
                className="w-12 sm:w-16 p-1 border rounded text-center text-sm sm:text-lg font-bold text-yellow-700"
              />
            </div>
            <div className="bg-red-100 p-2 sm:p-4 rounded-lg text-center">
              <div className="text-lg sm:text-2xl font-bold text-red-700">&lt;{scoringMatrix.refer_threshold}</div>
              <div className="text-xs sm:text-sm">Fail</div>
            </div>
          </div>

          <div className="bg-blue-50 p-3 sm:p-4 rounded-lg space-y-3">
            <h3 className="font-semibold text-sm sm:text-base">Income Requirements</h3>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs sm:text-sm">Min income-to-rent:</span>
              <input
                type="number"
                step="0.1"
                value={scoringMatrix.min_income_to_rent_ratio}
                onChange={(e) => setScoringMatrix({...scoringMatrix, min_income_to_rent_ratio: parseFloat(e.target.value)})}
                disabled={!editingScoring}
                className="w-16 p-1.5 text-sm border rounded"
              />
              <span className="font-bold">x</span>
            </div>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={scoringMatrix.allow_guarantor_override}
                onChange={(e) => setScoringMatrix({...scoringMatrix, allow_guarantor_override: e.target.checked})}
                disabled={!editingScoring}
                className="w-4 h-4"
              />
              <span className="text-xs sm:text-sm">Allow guarantor override</span>
            </label>
          </div>

          {editingScoring && (
            <div className="flex flex-col sm:flex-row gap-2 pt-2">
              <Button variant="outline" onClick={() => { fetchAllConfigs(); setEditingScoring(false); }} className="w-full sm:w-auto">Cancel</Button>
              <Button onClick={updateScoringMatrix} className="w-full sm:w-auto"><Save className="w-4 h-4 mr-2" />Save</Button>
            </div>
          )}
        </div>
      )}
    </Card>
  );

  const renderJurisdictions = () => (
    <Card className="p-4 sm:p-6">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-4 sm:mb-6">
        <h2 className="text-lg sm:text-2xl font-bold">Jurisdiction Rules</h2>
        <Button 
          onClick={() => setEditingJurisdictions(!editingJurisdictions)} 
          size="sm"
          variant={editingJurisdictions ? "outline" : "default"}
          className="w-full sm:w-auto"
        >
          {editingJurisdictions ? <><X className="w-4 h-4 mr-1" />Done</> : <><Edit2 className="w-4 h-4 mr-1" />Edit</>}
        </Button>
      </div>

      <div className="space-y-3">
        {jurisdictions.map((jurisdiction, idx) => (
          <div key={jurisdiction.jurisdiction} className="border rounded-lg p-3 sm:p-4">
            <h3 className="text-sm sm:text-lg font-bold capitalize mb-3">{jurisdiction.jurisdiction}</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
              {[
                { key: 'deposit_protection_required', label: 'Deposit Protection' },
                { key: 'right_to_rent_required', label: 'Right to Rent' },
                { key: 'gas_safety_required', label: 'Gas Safety' },
                { key: 'epc_required', label: 'EPC Required' }
              ].map(item => (
                <label key={item.key} className="flex items-center gap-2 p-2 bg-slate-50 rounded">
                  <input
                    type="checkbox"
                    checked={jurisdiction[item.key]}
                    onChange={(e) => {
                      const newJurisdictions = [...jurisdictions];
                      newJurisdictions[idx][item.key] = e.target.checked;
                      setJurisdictions(newJurisdictions);
                      if (editingJurisdictions) updateJurisdiction(newJurisdictions[idx]);
                    }}
                    disabled={!editingJurisdictions}
                    className="w-4 h-4"
                  />
                  <span className="text-xs sm:text-sm font-medium">{item.label}</span>
                </label>
              ))}
            </div>
          </div>
        ))}
      </div>
    </Card>
  );

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Mobile-first header */}
      <div className="sticky top-0 z-10 bg-white border-b px-4 py-3 sm:px-6 sm:py-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-3 mb-2">
            <Link to="/admin/console">
              <Button variant="ghost" size="sm" className="p-2">
                <ArrowLeft className="w-4 h-4" />
              </Button>
            </Link>
            <div className="flex-1 min-w-0">
              <h1 className="text-lg sm:text-2xl font-bold truncate">Platform Config</h1>
              <p className="text-xs sm:text-sm text-slate-600 hidden sm:block">Manage fees, markup, scoring & jurisdictions</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-6 pb-24 sm:pb-6">
        {/* Tabs - horizontal scroll on mobile */}
        <div className="flex gap-1 mb-4 sm:mb-6 overflow-x-auto pb-2 -mx-4 px-4 sm:mx-0 sm:px-0">
          {['fees', 'markup', 'scoring', 'jurisdictions'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-3 sm:px-4 py-2 text-sm font-medium whitespace-nowrap rounded-lg transition-colors ${
                activeTab === tab
                  ? 'bg-primary text-white'
                  : 'bg-white text-slate-600 hover:bg-slate-100 border'
              }`}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex flex-col items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            <p className="mt-3 text-sm text-slate-600">Loading...</p>
          </div>
        ) : (
          <div>
            {activeTab === 'fees' && renderFeeMatrix()}
            {activeTab === 'markup' && renderRepairMarkup()}
            {activeTab === 'scoring' && renderScoringMatrix()}
            {activeTab === 'jurisdictions' && renderJurisdictions()}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminConfig;
